import { useState, useEffect, useMemo } from 'react';

// Calcula minutos considerando expediente e/ou fim de semana
function calculateAdjustedMinutes(startDate, endDate, options = {}) {
  const {
    onlyBusinessHours = false,
    ignoreWeekends = false,
    businessStartHour = 8,
    businessEndHour = 20
  } = options;
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) return null;
  if (isNaN(startDate) || isNaN(endDate)) return null;
  if (endDate <= startDate) return 0;

  const isWeekend = (d) => {
    const day = d.getDay();
    return day === 0 || day === 6;
  };

  let totalMinutes = 0;
  let cursor = new Date(startDate.getTime());
  const end = new Date(endDate.getTime());

  while (cursor < end) {
    const day = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999);

    const segmentStart = new Date(cursor.getTime());
    const segmentEnd = new Date(Math.min(dayEnd.getTime(), end.getTime()));

    if (ignoreWeekends && isWeekend(day)) {
      // pula para o próximo dia útil
      cursor = new Date(dayEnd.getTime() + 1);
      continue;
    }

    const availableStart = onlyBusinessHours
      ? new Date(day.getFullYear(), day.getMonth(), day.getDate(), businessStartHour, 0, 0, 0)
      : new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);

    const availableEnd = onlyBusinessHours
      ? new Date(day.getFullYear(), day.getMonth(), day.getDate(), businessEndHour, 0, 0, 0)
      : new Date(dayEnd.getTime());

    const effectiveStart = new Date(Math.max(segmentStart.getTime(), availableStart.getTime()));
    const effectiveEnd = new Date(Math.min(segmentEnd.getTime(), availableEnd.getTime()));

    if (effectiveEnd > effectiveStart) {
      totalMinutes += Math.max(0, Math.round((effectiveEnd - effectiveStart) / (1000 * 60)));
    }

    // avança para o próximo dia
    cursor = new Date(dayEnd.getTime() + 1);
  }

  return Math.max(0, Math.round(totalMinutes));
}

/**
 * Hook para gerenciar filtros de dados
 */
export const useDataFilters = (data) => {
  const [filters, setFilters] = useState({
    origem: '',
    atendentes: [], // [] = todos selecionados
    dataInicio: '',
    dataFim: '',
    status: '',
    ignorarFimSemana: false,
    apenasExpediente: false
  });
  
  const [filteredData, setFilteredData] = useState([]);

  // Lista de atendentes únicos
  const attendantsList = useMemo(() => {
    const atendentes = new Set();
    data.forEach(item => {
      if (item.atendenteResponsavel && item.atendenteResponsavel !== 'Não atribuído') {
        const v = item.atendenteResponsavel;
        const label = v.includes('@') ? v.split('@')[0] : v;
        atendentes.add(label);
      }
    });
    return Array.from(atendentes).sort();
  }, [data]);

  useEffect(() => {
    let filtered = [...data];
    
    if (filters.origem) {
      filtered = filtered.filter(item => 
        item.origem.toLowerCase().includes(filters.origem.toLowerCase())
      );
    }
    
    if (filters.atendentes && filters.atendentes.length > 0) {
      const selecionados = new Set(filters.atendentes);
      filtered = filtered.filter(item => {
        const v = item.atendenteResponsavel || '';
        const label = v.includes('@') ? v.split('@')[0] : v;
        return selecionados.has(label);
      });
    }
    
    if (filters.status === 'com_resposta') {
      filtered = filtered.filter(item => item.temResposta);
    } else if (filters.status === 'sem_resposta') {
      filtered = filtered.filter(item => !item.temResposta);
    }
    
    // Intervalo de datas (início/fim) - considera apenas a data de conversão
    if (filters.dataInicio || filters.dataFim) {
      const start = filters.dataInicio ? new Date(`${filters.dataInicio}T00:00:00`) : null;
      const end = filters.dataFim ? new Date(`${filters.dataFim}T23:59:59.999`) : null;
      filtered = filtered.filter(item => {
        const d = item.dataConversao;
        if (!(d instanceof Date)) return false;
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
      });
    }
    
    if (filters.ignorarFimSemana) {
      filtered = filtered.filter(item => {
        if (!item.dataConversao) return true;
        const diaSemana = item.dataConversao.getDay();
        return diaSemana !== 0 && diaSemana !== 6;
      });
    }
    
    if (filters.apenasExpediente) {
      filtered = filtered.filter(item => {
        if (!item.dataConversao) return true;
        const hora = item.dataConversao.getHours();
        return hora >= 8 && hora <= 20;
      });
    }
    
    // Ajusta minutos de resposta conforme flags
    const shouldAdjust = filters.ignorarFimSemana || filters.apenasExpediente;
    if (shouldAdjust) {
      filtered = filtered.map((item) => {
        if (item && item.dataMensagemLead && item.dataMensagemAtendente) {
          const adjusted = calculateAdjustedMinutes(
            item.dataMensagemLead,
            item.dataMensagemAtendente,
            {
              onlyBusinessHours: !!filters.apenasExpediente,
              ignoreWeekends: !!filters.ignorarFimSemana,
              businessStartHour: 8,
              businessEndHour: 20
            }
          );
          if (typeof adjusted === 'number') {
            return { ...item, minutosResposta: adjusted };
          }
        }
        return item;
      });
    }

    setFilteredData(filtered);
  }, [filters, data]);

  return {
    filters,
    setFilters,
    filteredData,
    attendantsList
  };
};
